/**
 * Offline punch queue: clock events that could not reach Supabase, persisted
 * in localStorage and replayed when the connection comes back.
 *
 * Deliberately NOT part of the outbox. The outbox is last-write-wins on a
 * row+field, which is right for a note a crew member keeps editing in a
 * basement. Punches are the opposite: an ordered log of moments, where
 * "in 08:02, out 11:40, in 12:15" must replay in that order and none may be
 * collapsed. So this is a FIFO event queue with its own store.
 *
 * Every entry carries `occurredAt` — the instant the crew member actually
 * tapped. The RPCs take that as p_occurred_at and clamp it to [now-24h, now]
 * server-side (clamp_client_punch_ts), marking the row source='offline_queue'.
 * Without it a punch replayed on reconnect would be stamped with the reconnect
 * time and the shift would silently shrink.
 *
 * Flushing is sequential and stops at the first network failure, so the rest
 * wait for the next trigger. A permanent failure (locked payroll week, deleted
 * WO, RLS) drops that one entry and tells the user, so one poisoned punch can
 * never wedge the queue behind it.
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { supabase } from '@/lib/supabase/client'
import { useAppStore } from '@/stores/app-store'
import { isAuthError, isNetworkError, isOnline, withTimeout } from './network'

const RPC_TIMEOUT_MS = 12_000

export type PunchKind = 'in' | 'out'

export interface PunchQueueEntry {
  id: string
  kind: PunchKind
  /** Required for 'in'; null for 'out'. */
  workOrderId: string | null
  /** Display only, so the tile can name the job while still queued. */
  workOrderTitle: string | null
  /** Epoch ms on the device when the crew member tapped. */
  occurredAt: number
  queuedAt: number
  attempts: number
  lastError?: string
}

export type QueuePunchInput = Pick<PunchQueueEntry, 'kind' | 'workOrderId' | 'workOrderTitle'> & {
  /** Defaults to now. */
  occurredAt?: number
}

const newId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`

interface PunchQueueState {
  entries: PunchQueueEntry[]
  syncing: boolean
  enqueue: (input: QueuePunchInput) => PunchQueueEntry
  flush: () => Promise<{ sent: number; failed: number }>
  clear: () => void
}

export const usePunchQueueStore = create<PunchQueueState>()(
  persist(
    (set, get) => ({
      entries: [],
      syncing: false,

      enqueue: (input) => {
        const entry: PunchQueueEntry = {
          id: newId(),
          kind: input.kind,
          workOrderId: input.workOrderId,
          workOrderTitle: input.workOrderTitle,
          occurredAt: input.occurredAt ?? Date.now(),
          queuedAt: Date.now(),
          attempts: 0,
        }
        set((s) => ({ entries: [...s.entries, entry] }))
        return entry
      },

      flush: async () => {
        if (get().syncing) return { sent: 0, failed: 0 }
        if (!isOnline()) return { sent: 0, failed: 0 }
        if (get().entries.length === 0) return { sent: 0, failed: 0 }

        set({ syncing: true })
        let sent = 0
        let failed = 0

        try {
          // Snapshot and walk in order. Stop at the first entry that could not
          // reach the server so ordering is preserved for the next attempt.
          for (const entry of [...get().entries]) {
            const occurredAtIso = new Date(entry.occurredAt).toISOString()
            try {
              const call =
                entry.kind === 'in'
                  ? supabase.rpc('crew_punch_in', {
                      p_work_order_id: entry.workOrderId!,
                      p_occurred_at: occurredAtIso,
                    })
                  : supabase.rpc('crew_punch_out', { p_occurred_at: occurredAtIso })

              const { error } = await withTimeout(
                call as unknown as Promise<{ error: unknown }>,
                RPC_TIMEOUT_MS,
                `punch ${entry.kind}`,
              )
              if (error) throw error

              set((s) => ({ entries: s.entries.filter((e) => e.id !== entry.id) }))
              sent += 1
            } catch (err) {
              if (isNetworkError(err) || isAuthError(err)) {
                // Still no service (or the session is mid-refresh). Leave this
                // entry and everything after it for the next trigger.
                set((s) => ({
                  entries: s.entries.map((e) =>
                    e.id === entry.id
                      ? { ...e, attempts: e.attempts + 1, lastError: (err as Error)?.message }
                      : e,
                  ),
                }))
                break
              }
              // Permanent: locked payroll week, WO deleted, RLS. Drop it rather
              // than wedge every later punch behind a write that can never land.
              set((s) => ({ entries: s.entries.filter((e) => e.id !== entry.id) }))
              failed += 1
              useAppStore.getState().addToast({
                type: 'error',
                title: 'A queued punch could not be saved',
                message:
                  `${entry.kind === 'in' ? 'Punch in' : 'Punch out'} from ` +
                  `${new Date(entry.occurredAt).toLocaleString()} was rejected: ` +
                  `${(err as Error)?.message ?? 'unknown error'}.`,
                duration: 10000,
              })
            }
          }
        } finally {
          set({ syncing: false })
        }

        return { sent, failed }
      },

      clear: () => set({ entries: [] }),
    }),
    {
      name: 'opsyri-punch-queue',
      partialize: (s) => ({ entries: s.entries }) as PunchQueueState,
    },
  ),
)

/**
 * What the queue implies about the crew member's current clock state, or null
 * when nothing is queued and the server row is the whole truth.
 *
 * Only the LAST queued event matters: punch in then out then in leaves you on
 * the clock, whatever the server still thinks.
 */
export function pendingPunchState(entries: PunchQueueEntry[]): {
  onClock: boolean
  workOrderId: string | null
  workOrderTitle: string | null
  since: number
} | null {
  if (entries.length === 0) return null
  const last = entries[entries.length - 1]
  return {
    onClock: last.kind === 'in',
    workOrderId: last.workOrderId,
    workOrderTitle: last.workOrderTitle,
    since: last.occurredAt,
  }
}
